import { useLocale } from "@hooks/useLocale";
import { QueryState } from "@components/ui/QueryState";
import { StatBand, type Stat } from "@components/ui/StatBand";
import { useGetEventTubularsQuery } from "@api/pApi";
import { fmtDepth, fmtNum } from "@lib/format";
import type { AssemblyOut, CasingOut, PipeTallyOut } from "@api/types";
import type { Locale } from "@i18n/locales";

/** Hole / casing diameter — quoted in its own UOM (inches by default, pattern A),
 * NOT a metres-based depth, so it is formatted plainly with its unit label. */
function fmtDiameter(size: number | null, uom: string | null, locale: Locale): string {
  if (size == null || !Number.isFinite(size)) return "—";
  const label = uom ?? "in";
  return `${fmtNum(size, locale, { maximumFractionDigits: 3 })} ${label}`;
}

/**
 * Tubulars panel — the event's assemblies / casing strings + pipe-tally rows
 * (§10.2, served by Oxbo-P). Read-through only; every value is as-entered in
 * the event's unit profile and the FE renders (no recompute). Also embeddable
 * in the DDR Tubulars tab.
 */
export function TubularsPanel({ eventId }: { eventId: string }) {
  const { t, locale } = useLocale();
  const { data, isLoading, isError, refetch } = useGetEventTubularsQuery(eventId, {
    skip: !eventId,
  });

  // Guard against a 200 with a non-array collection (wrong shape reads as empty
  // rather than throwing on .map).
  const assemblies: AssemblyOut[] = Array.isArray(data?.assemblies) ? data!.assemblies : [];
  const casing: CasingOut[] = Array.isArray(data?.casing) ? data!.casing : [];
  const pipeTally: PipeTallyOut[] = Array.isArray(data?.pipe_tally) ? data!.pipe_tally : [];

  const jointCount = pipeTally.length;
  // Prefer the last row's cumulative length; fall back to summing the joints.
  const lastTally = pipeTally[pipeTally.length - 1];
  const tallyLength = lastTally
    ? (lastTally.cum_length ?? pipeTally.reduce((s, r) => s + (r.length ?? 0), 0))
    : null;

  const stats: Stat[] = data
    ? [
        { label: t("tub.kpi.assemblies"), value: fmtNum(assemblies.length, locale) },
        { label: t("tub.kpi.casing"), value: fmtNum(casing.length, locale) },
        { label: t("tub.kpi.joints"), value: fmtNum(jointCount, locale) },
        { label: t("tub.kpi.tallyLength"), value: fmtDepth(tallyLength, locale), tone: "ops" },
      ]
    : [];

  return (
    <QueryState
      isLoading={isLoading}
      isError={isError}
      isEmpty={
        !!data && assemblies.length === 0 && casing.length === 0 && pipeTally.length === 0
      }
      emptyMessage={t("tub.noData")}
      onRetry={refetch}
    >
      <div className="page-head" style={{ marginBottom: 12 }}>
        <p className="subtitle">{t("tub.subtitle")}</p>
      </div>
      {stats.length ? <StatBand stats={stats} /> : null}

      {/* assemblies */}
      <div className="card" style={{ marginTop: 14 }}>
        <h3>{t("tub.assemblies")}</h3>
        {assemblies.length === 0 ? (
          <div className="empty-state">{t("tub.assemblies.empty")}</div>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th className="num">{t("tub.col.no")}</th>
                  <th>{t("tub.col.name")}</th>
                  <th>{t("tub.col.type")}</th>
                  <th>{t("tub.col.phase")}</th>
                  <th>{t("tub.col.casingLiner")}</th>
                  <th className="num">{t("tub.col.mdTop")}</th>
                  <th className="num">{t("tub.col.mdBase")}</th>
                  <th className="num">{t("tub.col.holeSize")}</th>
                  <th className="num">{t("tub.col.length")}</th>
                </tr>
              </thead>
              <tbody>
                {assemblies.map((a) => (
                  <tr key={a.id} data-owner="p">
                    <td className="num tab-nums">{a.assembly_no ?? "—"}</td>
                    <td>{a.assembly_name ?? "—"}</td>
                    <td>
                      <span className="mono">{a.string_type_entry_key ?? "—"}</span>
                    </td>
                    <td>{a.phase_entry_key ?? "—"}</td>
                    <td>
                      {a.is_casing_liner == null ? "—" : a.is_casing_liner ? t("tub.yes") : t("tub.no")}
                    </td>
                    <td className="num tab-nums">{fmtDepth(a.md_top, locale)}</td>
                    <td className="num tab-nums">{fmtDepth(a.md_base, locale)}</td>
                    <td className="num tab-nums">
                      {fmtDiameter(a.hole_size, a.hole_size_uom, locale)}
                    </td>
                    <td className="num tab-nums">{fmtDepth(a.length_total, locale)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* casing strings */}
      <div className="card" style={{ marginTop: 14 }}>
        <h3>{t("tub.casing")}</h3>
        {casing.length === 0 ? (
          <div className="empty-state">{t("tub.casing.empty")}</div>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th className="num">{t("tub.col.report")}</th>
                  <th>{t("tub.col.assembly")}</th>
                  <th>{t("tub.col.date")}</th>
                </tr>
              </thead>
              <tbody>
                {casing.map((c) => (
                  <tr key={c.id} data-owner="p">
                    <td className="num tab-nums">{c.report_no ?? "—"}</td>
                    <td>
                      <span className="mono">{c.assembly_id ?? "—"}</span>
                    </td>
                    <td>{fmtDate(c.date_report, locale)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* pipe tally */}
      <div className="card" style={{ marginTop: 14 }}>
        <h3>{t("tub.pipeTally")}</h3>
        {pipeTally.length === 0 ? (
          <div className="empty-state">{t("tub.pipeTally.empty")}</div>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th className="num">{t("tub.col.run")}</th>
                  <th className="num">{t("tub.col.joint")}</th>
                  <th className="num">{t("tub.col.seq")}</th>
                  <th className="num">{t("tub.col.length")}</th>
                  <th className="num">{t("tub.col.cumLength")}</th>
                  <th>{t("tub.col.status")}</th>
                </tr>
              </thead>
              <tbody>
                {pipeTally.map((p) => (
                  <tr key={p.id} data-owner="p">
                    <td className="num tab-nums">
                      {p.run_number ?? p.pipe_run_ref ?? "—"}
                    </td>
                    <td className="num tab-nums">{p.joint_number ?? "—"}</td>
                    <td className="num tab-nums">{p.sequence_no ?? "—"}</td>
                    <td className="num tab-nums">{fmtDepth(p.length, locale)}</td>
                    <td className="num tab-nums">{fmtDepth(p.cum_length, locale)}</td>
                    <td>
                      {p.is_out == null ? (
                        "—"
                      ) : (
                        <span className={`badge ${p.is_out ? "status-completion" : "status-drilling"}`}>
                          {p.is_out ? t("tub.status.out") : t("tub.status.in")}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </QueryState>
  );
}

/** A yyyy-mm-dd report date in the tenant's locale. */
function fmtDate(iso: string | null, locale: Locale): string {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(locale, { year: "numeric", month: "short", day: "numeric" });
}
